import { UTCDate } from '@date-fns/utc'
import { StructuredDataSchemaItem, StructuredDataType } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@repo/ui/command'
import { useAsyncData } from '@repo/ui/dataProvider'
import { H3 } from '@repo/ui/h3'
import { getLocale, useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Label } from '@repo/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/popover'
import { ScrollArea } from '@repo/ui/scrollArea'
import { Table, TableBody, TableCell, TableRow } from '@repo/ui/table'
import { cn } from '@repo/ui/utils'
import { format } from 'date-fns'
import {
  CalendarIcon,
  Check,
  ChevronsUpDown,
  HashIcon,
  SquareCheckBig,
  SquareIcon,
  XIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { SmartBlock } from '@/components/SmartBlock'
import { useDelayedLoader } from '@/hooks'
import { storeActions } from '@/store/actions'
import {
  BeneficiaryStructuredDataView,
  buildBeneficiaryStructuredDataView,
  getCurrentBeneficiary,
  getCurrentOrganization,
} from '@/store/helpers'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

const keys = ['gatheringData', 'analyzingData', 'structuringData']

export function BeneficiaryStructuredData() {
  const dispatch = useStoreDispatch()
  const { tc } = useT()

  const [selectedSectionLabel, setSelectedSectionLabel] = useState('')

  const {
    data: [beneficiary, organization],
    isLoading,
    isUninitialized,
    isError,
  } = useAsyncData([getCurrentBeneficiary, getCurrentOrganization])

  const sectionLabels = useMemo(
    () => organization?.structuredDataDisplay.map((v) => v.label) || [],
    [organization],
  )

  const sections = useMemo(
    () =>
      buildBeneficiaryStructuredDataView({
        display: organization?.structuredDataDisplay || [],
        schema: organization?.structuredDataSchema || [],
        values: beneficiary?.structuredData.value || [],
      }),
    [beneficiary, organization],
  )

  const isGeneratingStructuredData = useStoreState(
    (state) => state.assistant.isGeneratingBeneficiaryStructuredData,
  )
  const isGeneratingDelayed = useDelayedLoader(isGeneratingStructuredData)
  const isThinking = isUninitialized || isLoading || !beneficiary || isGeneratingDelayed

  const handleForceRefresh = () => {
    if (!beneficiary) return
    void dispatch(
      storeActions.beneficiary.refreshStructuredData({
        beneficiaryId: beneficiary.id,
        cacheOptions: { forceReset: true },
      }),
    )
  }

  if (isError) return null

  return (
    <SmartBlock
      isThinking={isThinking}
      onForceRefresh={handleForceRefresh}
      thinkingTransKeys={keys}
      title={tc('structuredData', { cfl: true, colon: true })}
      traceUrl={beneficiary?.structuredData.traceUrl}
      updatedAt={beneficiary?.updatedAt}
      variant="default"
    >
      {beneficiary && (
        <ScrollArea>
          <div className="flex flex-col">
            <Selector
              labels={sectionLabels}
              onSelect={setSelectedSectionLabel}
              selectedLabel={selectedSectionLabel}
            />
            {sections
              .filter((section) =>
                selectedSectionLabel === '' ? true : section.label === selectedSectionLabel,
              )
              .map((view, i) => (
                <div key={i} className="mt-8">
                  <H3 className="pb-4 pl-4">{view.label}</H3>
                  <TableComponent data={view} />
                </div>
              ))}
          </div>
        </ScrollArea>
      )}
    </SmartBlock>
  )
}

function Selector({
  labels,
  onSelect,
  selectedLabel,
}: {
  labels: string[]
  onSelect: (name: string) => void
  selectedLabel: string
}) {
  const { tc } = useT()
  const [open, setOpen] = useState(false)
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <div className="border-primary-50 mt-4 flex items-center gap-2 border-b bg-white pb-4 pl-4 shadow-sm">
        <Label className="text-base">{tc('filter', { cfl: true, colon: true })}</Label>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className="w-[300px] justify-between">
            {selectedLabel ? (
              <span className="font-semibold">
                {labels.find((label) => label === selectedLabel)}
              </span>
            ) : (
              <>{tc('select', { cfl: true })}</>
            )}
            <ChevronsUpDown className="size-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <Button
          size="icon"
          variant="outline"
          onClick={() => onSelect('')}
          disabled={selectedLabel === ''}
        >
          <XIcon className="size-4" />
        </Button>
      </div>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder={tc('search', { cfl: true })} className="h-9" />
          <CommandList>
            <CommandEmpty>{tc('result', { cfl: true, count: 0 })}</CommandEmpty>
            <CommandGroup>
              {labels.map((label) => (
                <CommandItem
                  key={label}
                  value={label}
                  onSelect={(currentName) => {
                    onSelect(currentName)
                    setOpen(false)
                  }}
                  className={cn('text-sm', selectedLabel === label && 'text-primary-500')}
                >
                  {label}
                  <Check
                    className={cn(
                      'text-primary-500 ml-auto size-4',
                      selectedLabel === label ? 'block' : 'hidden',
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function TableComponent({ data }: { data: BeneficiaryStructuredDataView | undefined }) {
  if (!data) return null
  return (
    <Table>
      <TableBody>
        {data.items.map((pair, j) => (
          <TableRow key={j}>
            <TableCell className="w-1/2 font-medium">{pair.label}</TableCell>
            <TableCell className="w-1/2">
              <DisplayValue value={pair.value} type={pair.type} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function DisplayValue({
  value,
  type,
}: {
  value: StructuredDataSchemaItem['defaultValue']
  type: StructuredDataType
}) {
  const Unknown = () => <span className="select-none opacity-30">{tc('unknown')}</span>
  const { tc } = useT()
  if (
    value === null ||
    value === undefined ||
    value === 'undefined' ||
    value === 'null' ||
    value === ''
  )
    return <Unknown />

  switch (type) {
    case 'enum':
      return <InlineCode>{value}</InlineCode>
    case 'multiselect': {
      if (!Array.isArray(value)) return <Unknown />
      return (
        <div className="space-x-2">
          {value.map((v, i) => (
            <InlineCode key={i}>{v}</InlineCode>
          ))}
        </div>
      )
    }
    case 'string':
      return <span>{value}</span>
    case 'number': {
      return (
        <div className="flex items-center gap-2">
          <HashIcon className="text-primary-400 size-4" />
          {value.toString()}
        </div>
      )
    }
    case 'date': {
      if (typeof value !== 'number') return value
      return (
        <div className="flex items-center gap-2">
          <CalendarIcon className="text-primary-400 size-4" />
          {format(new UTCDate(value), 'dd MMMM yyyy', {
            locale: getLocale(), // TODO: make getLocale a global helper
          })}
        </div>
      )
    }
    case 'boolean':
      return value ? <SquareCheckBig className="size-5" /> : <SquareIcon className="size-5" />
    default:
      return <Unknown />
  }
}
